import { useCallback, useEffect, useState } from "react";

import { Language } from "@/components/language/supported-languages";
import useAudioTranscription from "@/hooks/webspeechTranscribe";
import useVAD from "@/hooks/vad";

type AudioProcessor = "vad" | "webspeech" | "all" | undefined;

interface UseAudioInputProps {
  autoStart: boolean;
  languageProp: Language;
  disableRecording: boolean;
  onSpeechStart: () => void;
  onVADMisfire: () => void; // User started speaking but for too short, audio did not completed
}

const useAudioInput = ({
  autoStart,
  languageProp,
  disableRecording,
  onSpeechStart,
  onVADMisfire,
}: UseAudioInputProps) => {
  //   const [language, setLanguage] = useState<Language>(languageProp);
  const [shouldAccumulateAudio, setShouldAccumulateAudio] =
    useState<boolean>(true);
  const [newAudio, setNewAudio] = useState<Float32Array>(new Float32Array());
  const [accumulatedAudio, setAccumulatedAudio] = useState<Float32Array>(
    new Float32Array(),
  );
  const [audioReadyCounter, setAudioReadyCounter] = useState<number>(0);
  const [isAudioDetected, setIsAudioDetected] = useState<boolean>(false);
  const [webspeechActive, setWebspeechActive] = useState<boolean>(autoStart);

  const [transcriptState, setTranscriptState] = useState<string>("");

  // Change audio transcript language
  //   useEffect(() => {
  //     if (languageProp) {
  //       setLanguage(languageProp);
  //     }
  //   }, [languageProp]);

  useEffect(() => {
    if (autoStart) {
      start("all");
    } else {
      pause("all");
    }
  }, []);

  const setAudioAccumulation = useCallback((shouldAccumulate: boolean) => {
    setShouldAccumulateAudio(shouldAccumulate);
  }, []);

  // Internal state management
  const handleAudioEnd = useCallback(
    (audio: Float32Array) => {
      setNewAudio(audio);
      if (shouldAccumulateAudio) {
        setAccumulatedAudio((prev) => {
          const newAudio = new Float32Array(prev.length + audio.length);
          newAudio.set(prev);
          newAudio.set(audio, prev.length);
          return newAudio;
        });
      }
      // FIXED: Increment counter instead of toggle boolean
      // This ensures every audio end event triggers the effect in voicechat-page
      setAudioReadyCounter((prev) => prev + 1);
    },
    [shouldAccumulateAudio, disableRecording],
  );

  // HOOKS FOR AUDIO PROCESSING
  // Real time audio transcription when suppported by browser
  const {
    transcript,
    interimTranscript,
    startListening,
    stopListening,
    resetTranscript,
    isListening,
  } = useAudioTranscription({
    continuous: true,
    language: languageProp.key,
    interimResults: true,
    maxAlternatives: 1,
    autoStart: autoStart,
  });

  // Fallback (always running) voice audio detection to capture audio
  const { vad } = useVAD({
    // Gives time for webspeech transcription final text to be completed
    // + some redemption time for user if pronunciation is not that fluent
    redemptionMs: 1800,
    submitUserSpeechOnPause: true,
    onAudioEnd: handleAudioEnd,
    onSpeechStart: onSpeechStart,
    onVADMisfire: onVADMisfire,
  });

  // Functions to control hook externally
  const pause = useCallback(
    (source: AudioProcessor = "all") => {
      if (source === "all" || source === "webspeech") {
        stopListening();
        setWebspeechActive(false);
      }
      if (source === "all" || source === "vad") {
        void vad.pause();
      }
    },
    [vad, stopListening],
  );

  const start = useCallback(
    (source: AudioProcessor = "all") => {
      if (source === "all" || source === "webspeech") {
        startListening();
        setWebspeechActive(true);
      }
      if (source === "all" || source === "vad") {
        void vad.start();
      }
    },
    [vad, startListening],
  );

  const clear = useCallback(() => {
    // FIXED Phase 2.2: Auto-clear transcript to prevent memory leak
    resetTranscript();
    setTranscriptState("");
    setAccumulatedAudio(new Float32Array());
    setNewAudio(new Float32Array());
    setShouldAccumulateAudio(true);
  }, [resetTranscript]);

  useEffect(() => {
    setIsAudioDetected(vad.userSpeaking);
    // If user is speaking and webspeech is not listening then start it
    if (!isListening && webspeechActive) {
      startListening();
    }
  }, [vad.userSpeaking, isListening, webspeechActive, startListening]);

  // Accumulate transcript
  useEffect(() => {
    setTranscriptState((prev) => {
      const separator = prev && transcript ? " " : "";
      return prev + separator + transcript;
    });
  }, [transcript]);

  // If recording is disabled then move interim to final transcript and clear interim
  useEffect(() => {
    if (disableRecording) {
      setTranscriptState((prev) => {
        const separator = prev && interimTranscript ? " " : "";
        return prev + separator + interimTranscript;
      });
      // Clear interim transcript
      resetTranscript();
    }
  }, [disableRecording, interimTranscript]);

  return {
    transcriptState,
    interimTranscript,
    isAudioDetected,
    newAudio,
    audioReadyCounter,
    accumulatedAudio,
    isVadListening: vad.listening,
    isWebspeechListening: isListening,
    isLoading: vad.loading,
    setAudioAccumulation,
    pause,
    start,
    clear,
  };
};

export default useAudioInput;
